import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createManagedSecret,
  deleteManagedSecret,
  removeManagedSecretAccess,
  setManagedSecretAccess,
  setManagedSecretValue,
  type CreateManagedSecretInput,
  type SetManagedSecretAccessInput,
} from "./api";
import { managedSecretKeys } from "./queries";

export function useCreateManagedSecretMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateManagedSecretInput) => createManagedSecret(input),
    onSuccess: async (secret) => {
      await queryClient.invalidateQueries({
        queryKey: managedSecretKeys.list(secret.organizationId),
      });
    },
  });
}

export function useSetManagedSecretValueMutation(secretId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (value: string) => setManagedSecretValue(secretId, value),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: managedSecretKeys.detail(secretId),
        }),
        queryClient.invalidateQueries({
          queryKey: managedSecretKeys.versions(secretId),
        }),
        queryClient.invalidateQueries({
          queryKey: managedSecretKeys.lists(),
        }),
      ]);
    },
  });
}

export function useSetManagedSecretAccessMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetManagedSecretAccessInput) =>
      setManagedSecretAccess(input),
    onSuccess: (secret) => {
      queryClient.setQueryData(managedSecretKeys.detail(secret.id), secret);
    },
  });
}

export function useRemoveManagedSecretAccessMutation(secretId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => removeManagedSecretAccess(secretId, userId),
    onSuccess: (secret) => {
      queryClient.setQueryData(managedSecretKeys.detail(secret.id), secret);
    },
  });
}

export function useDeleteManagedSecretMutation(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (secretId: string) => deleteManagedSecret(secretId),
    onSuccess: async (_data, secretId) => {
      queryClient.removeQueries({
        queryKey: managedSecretKeys.detail(secretId),
      });
      await queryClient.invalidateQueries({
        queryKey: managedSecretKeys.list(organizationId),
      });
    },
  });
}
